export type MeasureId =
  | 'count'
  | 'min'
  | 'max'
  | 'range'
  | 'mean'
  | 'median'
  | 'mode'
  | 'q1'
  | 'q3'
  | 'iqr'
  | 'variance'
  | 'stddev'
  | 'skewness';

export interface MeasureDef {
  readonly id: MeasureId;
  readonly label: string;
  /** KaTeX inline symbol, e.g. "$n$" */
  readonly symbolLatex: string;
  /** One-line plain-text description shown at the top of the expansion panel */
  readonly description: string;
  /** Symbolic formula in KaTeX, e.g. "$\\mu = \\frac{\\sum x_i}{n}$" */
  readonly formulaLatex: string;
}

export interface MeasureResult {
  readonly id: MeasureId;
  readonly value: number | number[] | null;
  readonly formattedValue: string;
  /** Formula with actual data plugged in, result highlighted inline */
  readonly pluggedInLatex: string;
}

export interface MeasureHighlightState {
  readonly hoveredMeasure: MeasureId | null;
  readonly pinnedMeasures: ReadonlySet<MeasureId>;
}

/** Colors matching the chart overlay / grid highlight colors for each measure. */
export const MEASURE_COLORS: Readonly<Record<MeasureId, string>> = {
  count: '#9ca3af', // gray-400
  min: '#f97316', // orange-500
  max: '#f97316', // orange-500
  range: '#f97316', // orange-500
  mean: '#f43f5e', // rose-500
  median: '#14b8a6', // teal-500
  mode: '#10b981', // emerald-500
  q1: '#8b5cf6', // violet-500
  q3: '#8b5cf6', // violet-500
  iqr: '#8b5cf6', // violet-500
  variance: '#0ea5e9', // sky-500
  stddev: '#06b6d4', // cyan-500
  skewness: '#6366f1', // indigo-500
};

export const MEASURE_DEFS: readonly MeasureDef[] = [
  {
    id: 'count',
    label: 'Count',
    symbolLatex: '$n$',
    description: 'The total number of data values in the set.',
    formulaLatex: '$n = \\text{number of data points}$',
  },
  {
    id: 'min',
    label: 'Minimum',
    symbolLatex: '$x_{\\min}$',
    description: 'The smallest value in the data set.',
    formulaLatex: '$x_{\\min} = \\min(x_1, x_2, \\ldots, x_n)$',
  },
  {
    id: 'max',
    label: 'Maximum',
    symbolLatex: '$x_{\\max}$',
    description: 'The largest value in the data set.',
    formulaLatex: '$x_{\\max} = \\max(x_1, x_2, \\ldots, x_n)$',
  },
  {
    id: 'range',
    label: 'Range',
    symbolLatex: '$R$',
    description: 'The difference between the largest and smallest values. Shows the total spread of the data.',
    formulaLatex: '$R = x_{\\max} - x_{\\min}$',
  },
  {
    id: 'mean',
    label: 'Mean',
    symbolLatex: '$\\mu$',
    description: 'The average of all values. Add them up and divide by how many there are.',
    formulaLatex: '$\\mu = \\dfrac{\\sum_{i=1}^{n} x_i}{n}$',
  },
  {
    id: 'median',
    label: 'Median',
    symbolLatex: '$\\tilde{x}$',
    description: 'The middle value when data is sorted. Exactly half the values are below it and half above.',
    formulaLatex:
      '$\\tilde{x} = \\begin{cases} x_{\\frac{n+1}{2}} & \\text{if } n \\text{ is odd} \\\\ \\frac{x_{\\frac{n}{2}} + x_{\\frac{n}{2}+1}}{2} & \\text{if } n \\text{ is even} \\end{cases}$',
  },
  {
    id: 'mode',
    label: 'Mode',
    symbolLatex: '$M_o$',
    description: 'The value that appears most often. A data set can have one mode, several, or none.',
    formulaLatex: '$M_o = \\text{most frequent value(s)}$',
  },
  {
    id: 'q1',
    label: 'Q1 (25th %ile)',
    symbolLatex: '$Q_1$',
    description: 'The value below which 25% of the data falls. The median of the lower half.',
    formulaLatex: '$Q_1 = \\text{value at position } \\tfrac{n+1}{4}$',
  },
  {
    id: 'q3',
    label: 'Q3 (75th %ile)',
    symbolLatex: '$Q_3$',
    description: 'The value below which 75% of the data falls. The median of the upper half.',
    formulaLatex: '$Q_3 = \\text{value at position } \\tfrac{3(n+1)}{4}$',
  },
  {
    id: 'iqr',
    label: 'Interquartile Range',
    symbolLatex: '$\\text{IQR}$',
    description: 'The spread of the middle 50% of data. Less affected by outliers than the range.',
    formulaLatex: '$\\text{IQR} = Q_3 - Q_1$',
  },
  {
    id: 'variance',
    label: 'Variance',
    symbolLatex: '$\\sigma^2$',
    description: 'The average of the squared differences from the mean. Measures how far data points are spread out.',
    formulaLatex: '$\\sigma^2 = \\dfrac{\\sum_{i=1}^{n} (x_i - \\mu)^2}{n}$',
  },
  {
    id: 'stddev',
    label: 'Std. Deviation',
    symbolLatex: '$\\sigma$',
    description: 'The square root of the variance. Tells you the typical distance of a data point from the mean.',
    formulaLatex: '$\\sigma = \\sqrt{\\dfrac{\\sum_{i=1}^{n} (x_i - \\mu)^2}{n}}$',
  },
  {
    id: 'skewness',
    label: 'Skewness',
    symbolLatex: '$\\gamma_1$',
    description: 'Measures whether data is symmetric or has a longer tail on one side.',
    formulaLatex: '$\\gamma_1 = \\dfrac{\\frac{1}{n}\\sum_{i=1}^{n} (x_i - \\mu)^3}{\\sigma^3}$',
  },
];
